import anyLeague from "../../classificators/league/anyLeague.js";
import battingScopeContext from "../../classificators/resultsSlice/battingScopeContext.js";
import pitchingScopeContext from "../../classificators/resultsSlice/pitchingScopeContext.js";
import resultsSliceForPitchType from "../../classificators/resultsSlice/resultsSliceForPitchType.js";
import satisfiesAll from "../../classificators/satisfiesAll.js";
import sessionWithPitch from "../../classificators/session/sessionWithPitch.js";
import IterableDataProcessor from "../../entities/IterableDataProcessor.js";
import RankingStat from "../../entities/RankingStat.js";
import BattingRankingStats from "../../enums/BattingRankingStats.js";
import DataSection from "../../enums/DataSection.js";
import PitchData from "../../enums/PitchData.js";
import PitchingRankingStats from "../../enums/PitchingRankingStats.js";
import RankingScopeContext from "../../enums/RankingScopeContext.js";
import ResultData from "../../enums/ResultData.js";
import ResultType from "../../enums/ResultType.js";
import StatType from "../../enums/StatType.js";
import ClassifiedPitchesFilter from "../../filters/contextual/ClassifiedPitchesFilter.js";
import ContextualFilterBase from "../../filters/contextual/ContextualFilterBase.js";
import NumberFormatter from "../../formatters/NumberFormatter.js";
import EnumValueProvider from "../../providers/EnumValueProvider.js";
import RankingStatCaseVerifier from "../../validators/RankingStatCaseVerifier.js";
import ResultsSlice from "../../valueObjects/ResultsSlice.js";
import RankingStatCalculatorBase from "./RankingStatCalculatorBase.js";

/**
 * Unfortunately this cannot be used in this way. Percentage data makes future use of this data very limited.
 * We would need to generate pitch usage for EACH possible pitch type. Adding / removing one pitch type from the available types would require re-calculation of everything.
 * Just imagine that pitchers thrown 20 fastballs in game 1. In game 2 he thrown 20 fastballs and 10 curveballs.
 * If we want to get pitch usage of Fastballs it will be OK.
 * But for curveballs we would needs to store 0% pitch usage for curveball in game 1. Otherwise cumulated valued will be off.
 * If we would have 4-5 pitch types and this definition would be fixed, we could add fake parameters with 0 value in order to provide proper cumulated values.
 * Unfortunately non of mentioned assumptions are true.
 */
class PitchUsageCalculator extends RankingStatCalculatorBase {
    constructor(context, customResultsProcessor, rankingStatCaseVerifier, numberFormatter, skipCache, contextualResultFilter) {
        if (typeof(customResultsProcessor) !== 'undefined' && !(customResultsProcessor instanceof IterableDataProcessor)) {
            throw new TypeError('customResultsProcessor must be an instance inheriting from IterableDataProcessor class');
        }
        if (typeof(numberFormatter) !== 'undefined' && !(numberFormatter instanceof NumberFormatter)) {
            throw new TypeError('numberFormatter must be an instance inheriting from NumberFormatter class');
        }
        if (typeof(contextualResultFilter) !== 'undefined' && !(contextualResultFilter instanceof ContextualFilterBase)) {
            throw new TypeError('contextualResultFilter must be an instance inheriting from ContextualFilterBase class');
        }
        if (!context || typeof(context) !== 'string') {
            throw new TypeError('context must be an non empty string');
        }
        if (![RankingScopeContext.Pitching.key, RankingScopeContext.Batting.key].find(s => s === context)) {
            throw new Error(`type of context ${context} in not supported, only ${RankingScopeContext.Pitching.key} and ${RankingScopeContext.Batting.key} are supported`);
        }

        super(
            [ context === RankingScopeContext.Pitching.key
                ? PitchingRankingStats.PitchUsage
                : BattingRankingStats.PitchUsage
            ],
            rankingStatCaseVerifier || new RankingStatCaseVerifier(
                anyLeague,
                sessionWithPitch,
                satisfiesAll(
                    context === RankingScopeContext.Pitching.key 
                        ? pitchingScopeContext
                        : battingScopeContext
                    ,
                    resultsSliceForPitchType
                )
            )
        );

        this.usageCache = [];
        this.skipCache = !!skipCache;
        this.numberFormatter = numberFormatter || new NumberFormatter(3);
        this.resultsProcessor = customResultsProcessor || new IterableDataProcessor(
            new EnumValueProvider()
        );
        this.contextualFilter = contextualResultFilter || new ClassifiedPitchesFilter(this.resultsProcessor);
        this.context = context;
    }

    /**
     * Calculates given statistical parameter
     * @param {object} parameter - a parameter to calculate, object from Enum PitchingRankingStats, BattingRankingStats or GeneralRankingStats
     * @param {ResultsSlice} slice - result slice with scope
     * @param {object} session - session object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/session-v2.json
     * @param {object} league - league object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/league.json
     * @returns {RankingStat|null} RankingStat instance or null when calculation wasn't possible
     */
    calculateStat(parameter, slice, session, league) {
        const cached = this.skipCache
            ? null
            : this.usageCache.find(spr =>
                (spr.session === session || session.id === spr.session.id)
                && (spr.league === league || spr.league.id === league.id)
                && spr.results === slice.resultsForBatterHandedness
                && spr.stats 
                && spr.stats.hasOwnProperty(slice.scope.pitchType)
            )
        ;

        if (cached) {
            return cached.stats[slice.scope.pitchType];
        }

        const calculated = {
            session, league, results: slice.resultsForBatterHandedness,
            stats: this.forceCalculate(slice, session, league) || {}
        };
        if (!this.skipCache) {
            this.usageCache.push(calculated);
        }

        if (calculated && calculated.stats && calculated.stats.hasOwnProperty(slice.scope.pitchType)) {
            return calculated.stats[slice.scope.pitchType];
        }

        return null;
    }

    clearCache() {
        this.this.usageCache = [];
    }

    /**
     * Calculates given statistical parameter when it is not avaialble in cache
     * @param {ResultsSlice} slice - result slice with scope
     * @param {object} session - session object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/session-v2.json
     * @param {object} league - league object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/league.json
     * @returns {object} object with keys equal to ranking stat parameter name and RankingStat instance as value
     */
    forceCalculate(slice, session, league) {
        if (!(slice instanceof ResultsSlice)) {
            throw new TypeError('slice must be an instance inheriting from ResultsSlice class');
        }

        // let's calculate percentages for pitches
        const statsPitch = this.resultsProcessor.calcStats(
            [StatType.Ratio.key],
            this.filterResults(slice.resultsForBatterHandedness, session, league),
            // TODO - simplify it...
            [ [ [] ].concat(
                ResultData.getNewDataPath(PitchData.Classification.key, DataSection.Tagging.key, ResultType.Pitch.key)
            ) ],
            false
        );

        const statsPerPitchType = {};

        // TODO - not sure about stats per hit ?
        if (statsPitch && statsPitch.hasOwnProperty(PitchData.Classification.key)){
            const ratios = statsPitch[PitchData.Classification.key][StatType.Ratio.key];
            for (const pitchType in ratios) {
                if (!Object.hasOwnProperty.call(ratios, pitchType)) {
                    continue;
                }

                statsPerPitchType[pitchType] = new RankingStat(
                    this.supportedParams[0].key,
                    this.numberFormatter.format(ratios[pitchType][StatType.Ratio.key]),
                    this.supportedParams[0].aggregator,
                    ratios[pitchType][StatType.Count.key]
                );
            }
        }

        return statsPerPitchType;
    }

    filterResults(results, session, league) {
        return this.contextualFilter.filter(results, session, league);
    }
}

export default PitchUsageCalculator;